import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { format } from "date-fns";

import TopBar from "@/components/energy/TopBar";
import KPIGrid from "@/components/energy/KPIGrid";
import PhasePanel from "@/components/energy/PhasePanel";
import TrendChart from "@/components/energy/TrendChart";
import EnergyChart from "@/components/energy/EnergyChart";
import AlertsPanel from "@/components/energy/AlertsPanel";
import MaxDemandCard from "@/components/energy/MaxDemandCard";
import CostCard from "@/components/energy/CostCard";
import TodCostCard from "@/components/energy/TodCostCard";
import ReadingsTable from "@/components/energy/ReadingsTable";
import DateRangeBar from "@/components/energy/DateRangeBar";

export default function Dashboard() {
  const [latest, setLatest] = useState(null);
  const [stats, setStats] = useState(null);
  const [ts, setTs] = useState([]);
  const [alerts, setAlerts] = useState({ items: [], ts: null });
  const [health, setHealth] = useState({ mysql_connected: false });
  const [meterName, setMeterName] = useState("");

  const [range, setRange] = useState(() => {
    const end = new Date();
    const start = new Date(end.getTime() - 24 * 60 * 60 * 1000);
    return { start, end };
  });

  useEffect(() => {
    axios.get(`${API}/settings`)
      .then((r) => setMeterName(r.data.mysql_table || ""))
      .catch(() => {});
  }, []);

  const fetchAll = useCallback(async (rng) => {
    const start = rng.start.toISOString();
    const end = rng.end.toISOString();
    try {
      const [l, s, t, a, h] = await Promise.all([
        axios.get(`${API}/energy/latest`),
        axios.get(`${API}/energy/stats`, { params: { start, end } }),
        axios.get(`${API}/energy/timeseries`, { params: { start, end, limit: 200 } }),
        axios.get(`${API}/energy/alerts`),
        axios.get(`${API}/energy/health`),
      ]);
      setLatest(l.data); setStats(s.data); setTs(t.data.items || []);
      setAlerts({ items: a.data.items || [], ts: a.data.ts });
      setHealth(h.data);
    } catch {
      toast.error("Failed to load dashboard data");
    }
  }, []);

  useEffect(() => { fetchAll(range); }, [fetchAll, range]);

  // Auto-refresh latest + alerts every 15s
  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const [l, a] = await Promise.all([
          axios.get(`${API}/energy/latest`),
          axios.get(`${API}/energy/alerts`),
        ]);
        setLatest(l.data);
        setAlerts({ items: a.data.items || [], ts: a.data.ts });
      } catch { /* silent */ }
    }, 15000);
    return () => clearInterval(id);
  }, []);

  const updatedLabel = useMemo(() => {
    if (!latest?.Time) return "—";
    try { return format(new Date(latest.Time), "dd MMM yyyy · HH:mm:ss"); } catch { return latest.Time; }
  }, [latest]);

  return (
    <div className="min-h-screen text-white" data-testid="dashboard-root">
      <TopBar
        updatedLabel={updatedLabel}
        mysqlConnected={health.mysql_connected}
        meterName={meterName}
      />

      <main className="max-w-[1600px] mx-auto px-6 py-6 space-y-6">
        <DateRangeBar
          range={range}
          setRange={setRange}
          onRefresh={() => fetchAll(range)}
          startIso={range.start.toISOString()}
          endIso={range.end.toISOString()}
        />

        <KPIGrid latest={latest} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-4">
            <TrendChart data={ts} />
            <EnergyChart data={ts} />
          </div>
          <div className="space-y-4">
            <PhasePanel latest={latest} />
            <MaxDemandCard stats={stats} />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <AlertsPanel alerts={alerts.items} ts={alerts.ts} />
          </div>
          <div className="space-y-4">
            <CostCard startIso={range.start.toISOString()} endIso={range.end.toISOString()} />
            <TodCostCard startIso={range.start.toISOString()} endIso={range.end.toISOString()} />
          </div>
        </div>

        <ReadingsTable startIso={range.start.toISOString()} endIso={range.end.toISOString()} />

        <footer className="py-8 text-center label-tech opacity-60">
          ENERGY.OPS · {meterName || "METER"} · {health.mysql_connected ? "LIVE DATA" : "OFFLINE"}
        </footer>
      </main>
    </div>
  );
}
